/** 路由配置参数 */
export type RouteParams = {
  /** 埋点页面实体ID */
  pageID?: string;
  /** 页面额外附加参数 */
  pageParams?: Record<string, unknown>;
  /** 如果路由含有param，例如/foo/:fooID，则创建相应匹配规则，例如 { fooID: '[0-9]+' }，此时fooID以参数上传 */
  matchedRegx?: {
    [index: string]: string;
  };
  /** 是否将解析的路由参数附加到参数进行上报，默认true */
  appendRouteParam?: boolean;
  /** 某些参数需要根据sessionStorage中的值去生成 */
  funcs?: Record<string, () => any>;
};

/**
 * 路由配置
 */
export interface RoutesConfig {
  [index: string]: RouteParams;
}

/**
 * 通过href匹配对应路由
 * @param routes AioWeblog路由配置 weblog.routes
 * @param pageHref 页面href
 */
export function getMatchedRoute(
  routes: RoutesConfig,
  pageHref?: any
): [undefined | RouteParams, Record<string, unknown>] {
  const paramsFromPath: Record<string, unknown> = {};

  if (typeof pageHref === 'undefined') {
    return [undefined, paramsFromPath];
  }

  let url: RegExpMatchArray | null | string =
    typeof pageHref === 'string' ? pageHref : pageHref.page || '';
  url = (url as any).match(/https?:\/\/[^/]+([^?]*)/);
  url = url && url[1] ? url[1] : '';
  if (url !== '/') {
    url = url.replace(/(.*)\/$/, '$1');
  }

  // 匹配route
  const routeNames = Object.keys(routes);
  const matchedRouteName = routeNames.find((routeName) => {
    const { matchedRegx } = routes[routeName];
    let parsedRouteName = routeName.replace(/\/:([^/]+)/g, (_, s1) => {
      if (matchedRegx && matchedRegx[s1]) {
        return `/${matchedRegx[s1]}`;
      }
      return s1;
    });

    if (parsedRouteName !== '/') {
      parsedRouteName = parsedRouteName.replace(/(.*)\/$/, '$1');
    }

    const routeRegx = new RegExp(`${parsedRouteName}/?$`);

    return parsedRouteName === '/'
      ? parsedRouteName === url
      : routeRegx.test(url as string);
  });

  if (typeof matchedRouteName === 'undefined') {
    return [undefined, paramsFromPath];
  }

  // 将path中的ID等等提取成参数
  const splitUrl = url.split('/');
  matchedRouteName
    .replace(/(.*)\/$/, '$1')
    .split(/\/:?/)
    .forEach((subStr, index) => {
      if (subStr === splitUrl[index]) {
        return;
      }

      paramsFromPath[subStr] = splitUrl[index];
    });

  // 如果没配置pageID，默认使用routeName
  if (typeof routes[matchedRouteName].pageID === 'undefined') {
    // asign for the global aioWeblog route config
    // eslint-disable-next-line no-param-reassign
    routes[matchedRouteName].pageID = matchedRouteName;
  }

  return [routes[matchedRouteName], paramsFromPath];
}

/**
 * 返回weblogger urlMap配置函数
 */
export function getUrlMapFn(routes: RoutesConfig) {
  return function urlMapFn({ url, page, params }) {
    const [matchedRoute, routeParams] = getMatchedRoute(routes, url);
    if (!matchedRoute) {
      const pageID = typeof url === 'string' ? url : url?.page || page;
      return { page: pageID, params };
    }

    const { pageID, pageParams, funcs } = matchedRoute;
    const funcParams = {};
    if (funcs) {
      Object.keys(funcs).forEach((key) => {
        funcParams[key] = funcs[key]()
      });
    }
    return {
      page: pageID,
      params:
        matchedRoute.appendRouteParam === false
          ? { ...pageParams, ...params, ...funcParams }
          : { ...pageParams, ...params, ...routeParams, ...funcParams }
    };
  };
}
